import { Product, ProductCategory } from 'api/types/product';
import { EPriceType } from 'components/NewProductStepper/ProductBaseForm/constants';
import _pick from 'lodash/pick';

export enum ProductLocalStatus {
  new = 'new',
  updated = 'updated',
}

export const isLocalProduct = (category: ProductCategory) =>
  category === ProductCategory.LP ||
  category === ProductCategory.POLP ||
  category === ProductCategory.LS ||
  category === ProductCategory.AF;

export const isPreorderProduct = (product: Product) =>
  product?.category === ProductCategory.PO || product?.category === ProductCategory.POLP;

export const isPreorderProductV2 = ({ category }: { category: ProductCategory }) =>
  category === ProductCategory.PO || category === ProductCategory.POLP;

export const validPreorderValue = (value) => typeof value === 'number' && value >= 0;

export const isRequest = (product: Product) => product?.requiresApproval;

export const isRequestV2 = ({ requiresApproval }: { requiresApproval: boolean }) =>
  requiresApproval;

export const isExternalLink = (product: Product) =>
  product?.category === ProductCategory.AF || product?.category === ProductCategory.LINK;

export const isExternalLinkV2 = ({ category }: { category: ProductCategory }) =>
  category === ProductCategory.AF || category === ProductCategory.LINK;

export const hasExtraFields = (product: Product) => product?.extraFields?.length > 0;

// A local product with multiple prices keeps its price and owner amount on each tier.
// Profit does not have to rise with price, so each "from" is its own floor.
export const getLocalProductPricing = (product: Product) => {
  const tiers = product?.productPrices ?? [];
  if (product?.priceType !== EPriceType.MULTIPLE || tiers.length === 0) {
    return {
      isFrom: false,
      price: product?.price,
      ownerLocalProductAmount: product?.ownerLocalProductAmount,
    };
  }

  const cheapestPrice = tiers.reduce((min, tier) =>
    Number(tier.price) < Number(min.price) ? tier : min,
  ).price;
  const amounts = tiers
    .map(({ ownerLocalProductAmount }) => ownerLocalProductAmount)
    .filter((amount) => amount != null);

  return {
    isFrom: true,
    price: String(cheapestPrice),
    ownerLocalProductAmount: amounts.length
      ? amounts.reduce((min, amount) => (Number(amount) < Number(min) ? amount : min))
      : product?.ownerLocalProductAmount,
  };
};

function getLocalStorage<T>(key): T {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? {};
  } catch (error) {
    return {} as T;
  }
}

const getStoreLocalProductsByOwner = (owner: number) => {
  return getLocalStorage<Record<string, Partial<Product>[]>>(`local_products_${owner}`);
};

const getNewStoreLocalProductsByOwner = (owner: number) => {
  return getLocalStorage<Record<string, Record<string, ProductLocalStatus>>>(
    `new_local_products_${owner}`,
  );
};

export const getLocalProductsByStore = (owner: number, storeId: number) => {
  const storeLocalProducts = getStoreLocalProductsByOwner(owner);
  return storeLocalProducts[storeId] ?? [];
};

export const getNewLocalProductsByStore = (owner: number, storeId: number) => {
  const storeLocalProducts = getNewStoreLocalProductsByOwner(owner);
  return storeLocalProducts[storeId] ?? {};
};

export const saveLocalProducts = (
  localProducts: Partial<Product>[],
  storeId: number,
  owner: number,
) => {
  const prevStoreLocalProducts = getStoreLocalProductsByOwner(owner);
  localStorage.setItem(
    `local_products_${owner}`,
    JSON.stringify({ ...prevStoreLocalProducts, [storeId]: localProducts }),
  );
};

export const saveNewLocalProducts = (
  localProducts: Record<string, ProductLocalStatus>,
  storeId: number,
  owner: number,
) => {
  const prevStoreLocalProducts = getNewStoreLocalProductsByOwner(owner);
  localStorage.setItem(
    `new_local_products_${owner}`,
    JSON.stringify({ ...prevStoreLocalProducts, [storeId]: localProducts }),
  );
};

export const removeNewLocalProductsFromStore = (owner, storeId) => {
  saveNewLocalProducts({}, storeId, owner);
};
